import { useCallback, useEffect, useId, useMemo, useRef, useState, type RefObject } from "react";
import { get, type StudentList, type StudentSummary } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";
import { scheduling, type SchedulingTutor } from "./scheduling.js";
import { staffBooking } from "./staff.js";
import { TutorBookLessonWithSetup } from "./TutorBookLessonWithSetup.js";

const PAGE_SIZE = 15;

export function BookOneLesson({
  appointmentMode = false,
  showLessonHeading = true,
  initialTutorId = "",
  initialStartsAt = null,
  suggestedStudents = [],
  studentSearchRef,
  onBooked = () => undefined,
  onMutationBusyChange,
}: {
  appointmentMode?: boolean;
  showLessonHeading?: boolean;
  initialTutorId?: string;
  initialStartsAt?: string | null;
  suggestedStudents?: readonly { studentId: string; name: string }[];
  studentSearchRef?: RefObject<HTMLInputElement>;
  onBooked?: () => void;
  onMutationBusyChange?: (busy: boolean) => void;
}) {
  const { me } = useAuth();
  const canChooseTutor = isOwner(me);
  const tutors = useAsync(
    () => (canChooseTutor ? scheduling.tutors() : Promise.resolve([])),
    [canChooseTutor],
  );
  const ownScheduling = useAsync(
    () => (canChooseTutor ? Promise.resolve(null) : scheduling.me()),
    [canChooseTutor],
  );
  const [ownerTutorId, setOwnerTutorId] = useState(initialTutorId);
  const [selectedStudent, setSelectedStudent] = useState<StudentSummary | null>(null);
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [suggestionBusy, setSuggestionBusy] = useState(false);
  const [suggestionNotice, setSuggestionNotice] = useState<string | null>(null);
  // A suggestion is confirmed by a server lookup. Any later choice (another
  // student, a new search, a tutor change) or unmount invalidates the pending
  // lookup so a slow answer cannot replace what the user has since chosen.
  const suggestionRequest = useRef(0);
  useEffect(() => () => { suggestionRequest.current += 1; }, []);
  function abandonSuggestion() {
    suggestionRequest.current += 1;
    setSuggestionBusy(false);
  }

  // The booking mutation owns the tutor/student context while it is in flight.
  // Track it here so the controls that would unmount the booking flow mid-call
  // (change student, change tutor) can be locked, and still forward it to a host
  // drawer that disables its own dismiss affordances. First-service assignment
  // setup counts as a mutation for the same reason.
  const handleMutationBusyChange = useCallback(
    (busy: boolean) => {
      setMutationBusy(busy);
      onMutationBusyChange?.(busy);
    },
    [onMutationBusyChange],
  );

  const selectedTutorId = canChooseTutor
    ? ownerTutorId
    : (ownScheduling.data?.tutorProfileId ?? "");
  const selectedStudentId = selectedStudent?.id ?? "";

  // Match the general Students screen: ask the server after a short pause,
  // rather than once per keystroke. A new search starts again at page one.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed]);

  // The server owns both matching and teaching scope. Tag each result so a
  // previous query can never remain selectable while the next one is pending
  // or loading: results count only when they answer exactly what is typed.
  const students = useAsync(
    async () => ({
      ...(await get<StudentList>("/admin/students", {
        q: query,
        page: String(page),
        limit: String(PAGE_SIZE),
        ...(canChooseTutor ? {} : { scope: "mine" }),
      })),
      query,
      requestedPage: page,
      accountId: me?.user.id,
    }),
    [query, page, me?.user.id, canChooseTutor],
  );
  const resultsCurrent = students.data?.query === typed && typed === query &&
    students.data?.requestedPage === page && students.data?.accountId === me?.user.id;
  const matches = resultsCurrent && !students.loading && !students.error
    ? students.data?.students ?? [] : [];
  function selectStudent(student: StudentSummary) {
    abandonSuggestion();
    setSelectedStudent(student);
    setActiveIndex(-1);
    setSuggestionNotice(null);
  }
  async function selectSuggestedStudent(suggestion: { studentId: string; name: string }) {
    if (suggestionBusy || mutationBusy) return;
    const request = ++suggestionRequest.current;
    setSuggestionBusy(true);
    setSuggestionNotice(null);
    setSelectedStudent(null);
    try {
      const result = await get<StudentList>("/admin/students", {
        q: suggestion.name,
        page: "1",
        limit: String(PAGE_SIZE),
        ...(canChooseTutor ? {} : { scope: "mine" }),
      });
      if (suggestionRequest.current !== request) return;
      const confirmed = result.students.find((student) => student.id === suggestion.studentId);
      if (confirmed) {
        selectStudent(confirmed);
      } else {
        setTyped("");
        setQuery("");
        setPage(1);
        setSuggestionsOpen(true);
        setSuggestionNotice("This student isn't available to book here; search instead.");
      }
    } catch {
      if (suggestionRequest.current !== request) return;
      setTyped("");
      setQuery("");
      setPage(1);
      setSuggestionsOpen(true);
      setSuggestionNotice("This student isn't available to book here; search instead.");
    } finally {
      if (suggestionRequest.current === request) setSuggestionBusy(false);
    }
  }
  const credits = useAsync(
    () =>
      selectedStudentId === ""
        ? Promise.resolve(null)
        : staffBooking.lessonCredits(selectedStudentId),
    [selectedStudentId],
  );

  const selectedTutor = useMemo<SchedulingTutor | null>(() => {
    if (canChooseTutor) {
      return tutors.data?.find((tutor) => tutor.tutorProfileId === ownerTutorId) ?? null;
    }
    const own = ownScheduling.data;
    if (!own?.tutorProfileId || own.providerId === null) return null;
    return {
      tutorProfileId: own.tutorProfileId,
      providerId: own.providerId,
      displayName: own.displayName ?? "You",
    };
  }, [canChooseTutor, ownerTutorId, ownScheduling.data, tutors.data]);

  // useAsync intentionally retains the previous successful value while a new
  // request is in flight. Keep an old student's credit snapshot out of a new
  // selection by checking the identity carried by the authoritative response.
  const creditData =
    credits.data?.portal_student_id === selectedStudentId ? credits.data : null;
  const blocks = useMemo(
    () =>
      selectedTutor === null
        ? []
        : (creditData?.blocks ?? []).filter((block) => block.provider_id === selectedTutor.providerId),
    [creditData, selectedTutor],
  );
  const pages = Math.max(1, Math.ceil((students.data?.total ?? 0) / PAGE_SIZE));

  function chooseTutor(nextTutorId: string) {
    if (!canChooseTutor) return;
    if (nextTutorId !== ownerTutorId) {
      abandonSuggestion();
      // The target tutor is part of the booking context. Force student/service/
      // availability to be chosen again rather than carrying state across an
      // on-behalf-of tutor change.
      setSelectedStudent(null);
      setTyped("");
      setQuery("");
      setActiveIndex(-1);
      setPage(1);
    }
    setOwnerTutorId(nextTutorId);
  }

  return (
    <div className="book-one-lesson">
      <div className="panel">
        {canChooseTutor ? (
          <label className="stack">
            <span className="small muted">Tutor</span>
            <select
              value={selectedTutorId}
              disabled={tutors.loading || mutationBusy}
              onChange={(event) => chooseTutor(event.target.value)}
            >
              <option value="">Select a tutor…</option>
              {(tutors.data ?? []).map((tutor) => (
                <option key={tutor.tutorProfileId} value={tutor.tutorProfileId}>
                  {tutor.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="stack">
            <span className="small muted">Tutor</span>
            <strong>
              {ownScheduling.loading
                ? "Loading tutor…"
                : selectedTutor?.displayName ?? "Tutor unavailable"}
            </strong>
            {initialTutorId !== "" &&
              selectedTutor !== null &&
              initialTutorId !== selectedTutor.tutorProfileId && (
                <span className="small muted">
                  The diary suggested another tutor. Lessons booked here are recorded against your own diary.
                </span>
              )}
          </div>
        )}
        {canChooseTutor && tutors.error && (
          <div className="error" style={{ marginTop: 10 }}>We could not load the available tutors.</div>
        )}
        {!canChooseTutor && ownScheduling.error && (
          <div className="error" style={{ marginTop: 10 }}>We could not load your tutor identity.</div>
        )}
      </div>

      <section>
        <h2>Student</h2>
        <div className="panel booking-student-selection">
          {selectedStudent ? (
            <div className="tutor-student-row is-selected" style={{ cursor: "default" }}>
              <span>
                <strong>{selectedStudent.fullName}</strong>
                <span className="small muted">
                  {selectedStudent.instrument ?? "Lessons"}
                  {selectedStudent.parentName ? ` · Parent: ${selectedStudent.parentName}` : ""}
                </span>
              </span>
              <button
                className="quiet small"
                type="button"
                disabled={mutationBusy}
                onClick={() => { abandonSuggestion(); setSelectedStudent(null); setSuggestionsOpen(true); }}
              >
                Change student
              </button>
            </div>
          ) : (
            <>
              {suggestedStudents.length > 0 && (
                <div className="stack" aria-label="Suggested students">
                  <span className="small muted">Suggested students</span>
                  <div className="row">
                    {suggestedStudents.map((suggestion) => (
                      <button
                        key={suggestion.studentId}
                        className="action-secondary"
                        type="button"
                        disabled={suggestionBusy || mutationBusy}
                        onClick={() => void selectSuggestedStudent(suggestion)}
                      >
                        {suggestion.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestionNotice && <p className="small muted" role="status">{suggestionNotice}</p>}
              <input
                ref={studentSearchRef}
                type="search"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestionsOpen && matches.length > 0}
                aria-controls={listId}
                aria-activedescendant={suggestionsOpen && matches[activeIndex] ? `${listId}-${activeIndex}` : undefined}
                onFocus={() => setSuggestionsOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    // Only claim Escape when it closes a visible list;
                    // otherwise it belongs to the host drawer. Preventing the
                    // default also stops Chrome and Safari clearing the search
                    // field, which would reopen the full list.
                    if (suggestionsOpen && matches.length > 0) {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                    setSuggestionsOpen(false);
                    setActiveIndex(-1);
                  } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    setSuggestionsOpen(true);
                    setActiveIndex((index) => matches.length === 0 ? -1 :
                      event.key === "ArrowDown" ? (index + 1) % matches.length :
                      (index <= 0 ? matches.length - 1 : index - 1));
                  } else if (event.key === "Enter" && suggestionsOpen && matches[activeIndex]) {
                    event.preventDefault();
                    selectStudent(matches[activeIndex]);
                  }
                }}
                aria-label="Search students"
                placeholder="Search students"
                value={typed}
                onChange={(event) => {
                  abandonSuggestion();
                  setTyped(event.target.value);
                  setActiveIndex(-1);
                  setSuggestionsOpen(true);
                }}
                style={{ width: "100%" }}
              />

              {!students.error && (students.loading || !resultsCurrent) && (
                <p className="empty" role="status">{students.data ? "Searching students…" : "Loading students…"}</p>
              )}
              {students.error && <div className="error" style={{ marginTop: 12 }}>We could not load students.</div>}
              {resultsCurrent && !students.loading && !students.error && matches.length === 0 && (
                <p className="empty">
                  {query === ""
                    ? "No students are available in your current teaching scope."
                    : "No students match that search."}
                </p>
              )}

              {suggestionsOpen && matches.length > 0 && (
                <div id={listId} role="listbox" aria-label="Matching students" className="tutor-student-list" style={{ marginTop: 12 }}>
                  {matches.map((student, index) => (
                    <button
                      className={`tutor-student-row${index === activeIndex ? " is-selected" : ""}`}
                      role="option"
                      id={`${listId}-${index}`}
                      aria-selected={index === activeIndex}
                      key={student.id}
                      type="button"
                      onClick={() => selectStudent(student)}
                    >
                      <span>
                        <strong>{student.fullName}</strong>
                        {student.parentName && (
                          <span className="small muted">Parent: {student.parentName}</span>
                        )}
                      </span>
                      <span className="small muted">{student.instrument ?? "Lessons"}</span>
                    </button>
                  ))}
                </div>
              )}

              {suggestionsOpen && resultsCurrent && !students.loading && !students.error && pages > 1 && (
                <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
                  <button className="quiet small" disabled={page <= 1} onClick={() => setPage(page - 1)} type="button">
                    Previous
                  </button>
                  <span className="small muted">Page {page} of {pages}</span>
                  <button className="quiet small" disabled={page >= pages} onClick={() => setPage(page + 1)} type="button">
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {selectedStudentId !== "" && selectedTutor !== null && (
        <section>
          <h2>Lesson</h2>
          {/*
           * Credit blocks only enrich the service names below; the bookable
           * options (including the zero-credit payment-pending path) come from
           * the server-owned offerings contract inside the booking flow, so a
           * slow or failed lesson-credit read must not gate the whole flow.
           */}
          {credits.error && (
            <p className="small muted">
              Lesson-credit details are unavailable right now; booking options still load from the booking system.
            </p>
          )}
          <TutorBookLessonWithSetup
            key={`${selectedStudentId}:${selectedTutor.tutorProfileId}`}
            studentId={selectedStudentId}
            studentName={selectedStudent?.fullName ?? null}
            blocks={blocks}
            selectedTutorProviderId={selectedTutor.providerId}
            selectedTutorName={selectedTutor.displayName}
            appointmentMode={appointmentMode}
            showHeading={showLessonHeading}
            initialStartsAt={appointmentMode ? null : initialStartsAt}
            onBooked={onBooked}
            onMutationBusyChange={handleMutationBusyChange}
          />
        </section>
      )}
    </div>
  );
}
